/**
 * Per-unit client accept/reject decision schemas (client-offer-decision-persistence).
 *
 * Covers:
 * - `unitDecisions` request payload shared by AcceptVersionBodySchema/RejectVersionBodySchema
 *   (`packages/contracts/src/schemas/offer-version-diff.ts`) — optional on both. Entries
 *   reject a duplicate nośnik (shape rule, enforced here). Whether an Accept payload names
 *   at least one ACCEPTED entry is NOT a shape rule: the validation pipe only ever answers
 *   400, but that precondition is specified as a 409, so it is owned by the two accept
 *   endpoints, not this schema.
 * - `recordedDecisions` response field on ClientOfferViewResponseSchema/
 *   PublicOfferViewResponseSchema — the persisted per-unit decisions for an accepted
 *   version, ACCEPTED/REJECTED only (a recorded row is never UNDECIDED).
 * - `UnitDecisionsListResponseSchema` for `GET /offers/:offerId/versions/:versionId/unit-decisions`.
 * - `ReleaseRejectedUnitsResponseSchema` for
 *   `POST /offers/:offerId/versions/:versionId/release-rejected-units` — frees holds
 *   only, never touches the draft.
 * - `RemoveRejectedUnitsResponseSchema` for
 *   `POST /offers/:offerId/versions/:versionId/remove-rejected-units` — removes the
 *   draft row and, in the same per-unit transaction, frees any hold the unit still
 *   carries. Removal implies release; release never removes (see the "Manual
 *   removal of rejected units from the offer draft" requirement).
 *
 * Traceability: REQ-7.17.
 */
import { z } from 'zod';
import { BatchReserveCellSchema } from './reservation-batch.js';

// Mirrors RESERVATION_BATCH_CAP's array-cap convention (reservation-batch.ts).
export const UNIT_DECISIONS_CAP = 500;

const UnitDecisionPeriodSchema = z.object({
  from: z.coerce.date(),
  to: z.coerce.date(),
});

/**
 * Shared `.refine()` predicate for every per-unit decision entry shape in this file
 * (UnitDecisionEntrySchema, CarryForwardPrefillEntrySchema, RecordedUnitDecisionEntrySchema):
 * `periods` is required and non-empty when `decision === 'ACCEPTED'`, and must be absent
 * otherwise — mirrors the DB's offer_version_unit_decisions_periods_check constraint exactly,
 * so a malformed entry fails validation with a clean 400 here instead of an uncaught Postgres
 * check-violation at insert time.
 */
function periodsRequiredIffAccepted(val: { decision: string; periods?: unknown[] }): boolean {
  return val.decision === 'ACCEPTED'
    ? val.periods != null && val.periods.length > 0
    : val.periods == null;
}
const PERIODS_REQUIRED_IFF_ACCEPTED_ISSUE = {
  message:
    'periods is required and non-empty when decision is ACCEPTED, and must be absent otherwise',
  path: ['periods'] as PropertyKey[],
};

// ─── Request payload (unitDecisions on Accept/Reject) ─────────────────────────

export const UnitDecisionSchema = z.enum(['ACCEPTED', 'REJECTED', 'UNDECIDED']);
export type UnitDecision = z.infer<typeof UnitDecisionSchema>;

/**
 * unitId is semantically mediaUnitId — the media unit's stable identifier, NOT
 * the offer-version-snapshot row id (which stops existing once a newer version
 * is published). periods is required (non-empty) when decision === 'ACCEPTED'
 * and must be absent/empty otherwise — mirrors the DB's
 * offer_version_unit_decisions_periods_check constraint exactly, so a
 * malformed entry fails validation with a clean 400 here instead of an
 * uncaught Postgres check-violation at insert time.
 */
export const UnitDecisionEntrySchema = z
  .object({
    unitId: z.number().int().positive(),
    decision: UnitDecisionSchema,
    periods: z.array(UnitDecisionPeriodSchema).optional(),
  })
  .strict()
  .refine(periodsRequiredIffAccepted, PERIODS_REQUIRED_IFF_ACCEPTED_ISSUE);
export type UnitDecisionEntry = z.infer<typeof UnitDecisionEntrySchema>;

// Rejects duplicate unitId within one submission, mirroring the DB's
// (version_id, media_unit_id) unique constraint — fails validation with a
// clean 400 instead of an uncaught Postgres unique-violation at insert time.
export const UnitDecisionsArraySchema = z
  .array(UnitDecisionEntrySchema)
  .max(UNIT_DECISIONS_CAP)
  .refine((entries) => new Set(entries.map((e) => e.unitId)).size === entries.length, {
    message: 'unitDecisions entries must have distinct unitId values',
  });

// ─── Read response (GET .../unit-decisions) ────────────────────────────────────

/**
 * Reservation state surfaced on the internal "Decyzje klienta" review table.
 * Mirrors ReservationStatusSchema (reservation-status.ts) plus the "no
 * reservation" case. HARD_RESERVATION is deliberately folded into
 * SOFT_RESERVATION for this display-only projection — both represent an
 * active internal hold, and every reservation this offer flow itself can
 * create is SOFT_RESERVATION or OPTION (see LockReservationsRequestSchema);
 * a HARD_RESERVATION appearing here would come from an unrelated admin flow
 * and should still read as "held", not silently show as unreserved.
 */
export const UnitDecisionReservationStateSchema = z.enum([
  'NONE',
  'SOFT_RESERVATION',
  'OPTION',
  'SOLD',
]);
export type UnitDecisionReservationState = z.infer<typeof UnitDecisionReservationStateSchema>;

export const UnitDecisionsListItemSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  decision: UnitDecisionSchema,
  periods: z.array(UnitDecisionPeriodSchema).optional(),
  // Display-only fields added for the "Decyzje klienta" review table
  // (client-offer-decision-persistence / Internal visibility of per-unit
  // client decisions). Sourced from the version snapshot's mediaUnitDetails
  // and prices — see OfferVersionsService.getUnitDecisions.
  address: z.string().nullable(),
  city: z.string().nullable(),
  grwNumber: z.string().nullable(),
  supplierNumber: z.string().nullable(),
  supplier: z.string().nullable(),
  mediaType: z.string().nullable(),
  dimensions: z.string().nullable(),
  offeredPricePln: z.number().nullable(),
  reservationState: UnitDecisionReservationStateSchema,
  grwId: z.string().nullable(),
  formatCode: z.string().nullable(),
  format: z.string().nullable(),
  region: z.string().nullable(),
  county: z.string().nullable(),
  agglomeration: z.string().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  dimensionNotes: z.string().nullable(),
  audienceEstimate: z.number().nullable(),
  notes: z.string().nullable(),
  hasPhoto: z.boolean(),
  /**
   * True when this row's supplier-identifying values (`supplier`/
   * `supplierNumber`) were nulled by the server's field-visibility gate
   * rather than genuinely absent (task 1.1, CU-86cbc3r8b) — see
   * field-visibility's "Restricted values are masked, not removed from the
   * layout". This projection carries no cost fields (the "Decyzje klienta"
   * review table has no cost column), so — unlike OfferUnitListItemSchema
   * (offer-unit.ts) and MediaUnitListItemSchema (media-units.ts), which carry
   * both `costsWithheld` and `supplierWithheld` — this schema needs only the
   * supplier dimension.
   *
   * `.default(false)` per the "Rollout and rollback" Decision (proposal.md):
   * an old, pre-deploy response omits this key entirely during the api-before-
   * web rollback window, and its absence MUST read as "not withheld" (false),
   * never as "values were withheld". `.default()` keeps the key optional on
   * the parse input while guaranteeing the parsed output is always boolean.
   */
  supplierWithheld: z.boolean().default(false),
});
export type UnitDecisionsListItem = z.infer<typeof UnitDecisionsListItemSchema>;

export const UnitDecisionsListResponseSchema = z.array(UnitDecisionsListItemSchema);
export type UnitDecisionsListResponse = z.infer<typeof UnitDecisionsListResponseSchema>;

// ─── Release-rejected-units action response ────────────────────────────────────
//
// Frees holds only — SHALL NOT touch offer_draft_units (see the "Manual
// release of rejected units' reservations" requirement's split from removal).

export const ReleasedUnitSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  // Array, not a single id: a unit split earlier by a selected-period release
  // can have more than one active reservation for the same unit+offer, and
  // ALL of them are released together in one `released` outcome.
  reservationIds: z.array(z.number().int().positive()),
});
export type ReleasedUnit = z.infer<typeof ReleasedUnitSchema>;

export const NothingToReleaseUnitSchema = z.object({
  mediaUnitId: z.number().int().positive(),
});
export type NothingToReleaseUnit = z.infer<typeof NothingToReleaseUnitSchema>;

export const FailedReleaseUnitSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  errorCode: z.string(),
});
export type FailedReleaseUnit = z.infer<typeof FailedReleaseUnitSchema>;

export const ReleaseRejectedUnitsResponseSchema = z.object({
  released: z.array(ReleasedUnitSchema),
  nothingToRelease: z.array(NothingToReleaseUnitSchema),
  failed: z.array(FailedReleaseUnitSchema),
});
export type ReleaseRejectedUnitsResponse = z.infer<typeof ReleaseRejectedUnitsResponseSchema>;

// ─── Remove-rejected-units action response ─────────────────────────────────────
//
// Removes the offer_draft_units row and, in the same per-unit transaction,
// frees any hold the unit still carries. A SOLD-tied unit is neither
// released nor removed and reports `failed`.

export const RemovedUnitSchema = z.object({
  mediaUnitId: z.number().int().positive(),
});
export type RemovedUnit = z.infer<typeof RemovedUnitSchema>;

// Byte-identical to FailedReleaseUnitSchema above — shared rather than
// redeclared; both actions report a failed unit as just its id and an
// error code.
export const FailedRemovalUnitSchema = FailedReleaseUnitSchema;
export type FailedRemovalUnit = FailedReleaseUnit;

export const RemoveRejectedUnitsResponseSchema = z.object({
  removed: z.array(RemovedUnitSchema),
  failed: z.array(FailedRemovalUnitSchema),
});
export type RemoveRejectedUnitsResponse = z.infer<typeof RemoveRejectedUnitsResponseSchema>;

// ─── Release-rejected-units preview (client-offer-decision-persistence) ────
//
// Read-only preview of what releasing a version's rejected nośniki would do:
// its hold released, nothing to release because it holds none, or the
// action could not resolve it. Never describes a nośnik as leaving the
// draft — the release action no longer removes anything (client-offer-
// decision-persistence / "Release-rejected-units preview"). GET, path
// params only — no request schema needed, same convention as
// ConfirmationPreviewResponseSchema/SalePreviewResponseSchema (campaign-sale.ts).

export const ReleaseRejectedPreviewItemSchema = z.discriminatedUnion('outcome', [
  BatchReserveCellSchema.extend({ outcome: z.literal('released') }),
  // Fixup (action-breakdown-remaining-dialogs task 2.1 review finding): NOT
  // BatchReserveCellSchema — a REJECTED unit decision persists no periods at
  // all (UnitDecisionEntrySchema's own invariant: periods required only when
  // ACCEPTED), and `nothing_to_release` by definition means no reservation
  // ever existed for this unit+offer either — so there is no window anywhere
  // in the domain to populate dateFrom/dateTo for this outcome. mediaUnitId only.
  z.object({
    mediaUnitId: z.number().int().positive(),
    outcome: z.literal('nothing_to_release'),
  }),
  BatchReserveCellSchema.extend({ outcome: z.literal('unresolvable'), reason: z.string() }),
]);
export type ReleaseRejectedPreviewItem = z.infer<typeof ReleaseRejectedPreviewItemSchema>;

export const ReleaseRejectedPreviewResponseSchema = z.array(ReleaseRejectedPreviewItemSchema);
export type ReleaseRejectedPreviewResponse = z.infer<typeof ReleaseRejectedPreviewResponseSchema>;

// ─── Remove-rejected-units preview (client-offer-decision-persistence) ─────
//
// Read-only preview of what removing a version's rejected nośniki from the
// draft would do. Every rejected nośnik either leaves the draft (freeing any
// hold it still carries in the same step) or, if sold, is left entirely
// unchanged and reported as unresolvable naming the sale.

export const RemoveRejectedPreviewItemSchema = z.discriminatedUnion('outcome', [
  // A window is present only when an active hold exists to free as part of
  // the removal; absent when there is nothing to release, mirroring
  // ReleaseRejectedPreviewItemSchema's own `nothing_to_release` convention.
  z.object({
    mediaUnitId: z.number().int().positive(),
    outcome: z.literal('removed'),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
  }),
  BatchReserveCellSchema.extend({ outcome: z.literal('unresolvable'), reason: z.string() }),
]);
export type RemoveRejectedPreviewItem = z.infer<typeof RemoveRejectedPreviewItemSchema>;

export const RemoveRejectedPreviewResponseSchema = z.array(RemoveRejectedPreviewItemSchema);
export type RemoveRejectedPreviewResponse = z.infer<typeof RemoveRejectedPreviewResponseSchema>;

// ─── Carry-forward pre-fill (offer-decision-carry-forward, REQ-7.17) ───────────

/**
 * A carry-forward pre-fill entry is a computed, non-persisted default for the
 * client's per-unit selection on a newly-viewed version — never written to
 * offer_version_unit_decisions. When carried forward FROM a prior version, only
 * ACCEPTED or UNDECIDED apply: a REJECTED unit is already removed from the draft
 * by the sibling change (CU-86carv42p) and is never carried forward across
 * versions. REJECTED is included here only to let the viewed version's OWN
 * already-persisted decision be reflected on reload (client-decisions-rehydrate-
 * on-reload bug fix, `OfferVersionsService.computeCarryForwardPrefill`'s
 * `applyOwnVersionDecisions` merge step) — it is never produced by the
 * prior-version classification path (`classifyCarryForwardUnit`). Attached as a
 * top-level sibling field on ClientOfferViewResponseSchema /
 * PublicOfferViewResponseSchema — NOT nested under their `version` sub-schema
 * (see offer-version-diff.ts / offer-public-link.ts).
 *
 * `locked` (offer-decision-carry-forward / Locked carry-forward entries are
 * enforced on finalize, REQ-7.17): `true` exactly when the entry is `ACCEPTED`
 * AND carried forward from the source version (offered terms unchanged) — the
 * client's prior choice on that unit is binding in the new version, not a
 * suggestion. Always `false` for `UNDECIDED` and for the viewed version's own
 * already-persisted-decision echo (`applyOwnVersionDecisions`). Derived
 * server-side on every request; never persisted or trusted from a client
 * payload — see `OfferVersionsService`'s finalize-time recomputation.
 */
export const CarryForwardPrefillDecisionSchema = z.enum(['ACCEPTED', 'REJECTED', 'UNDECIDED']);
export type CarryForwardPrefillDecision = z.infer<typeof CarryForwardPrefillDecisionSchema>;

export const CarryForwardPrefillEntrySchema = z
  .object({
    mediaUnitId: z.number().int().positive(),
    decision: CarryForwardPrefillDecisionSchema,
    locked: z.boolean(),
    periods: z.array(UnitDecisionPeriodSchema).optional(),
  })
  .strict()
  .refine(periodsRequiredIffAccepted, PERIODS_REQUIRED_IFF_ACCEPTED_ISSUE)
  .refine((val) => !(val.decision !== 'ACCEPTED' && val.locked), {
    message: 'locked can only be true when decision is ACCEPTED',
    path: ['locked'],
  });
export type CarryForwardPrefillEntry = z.infer<typeof CarryForwardPrefillEntrySchema>;

export const CarryForwardPrefillSchema = z.array(CarryForwardPrefillEntrySchema);
export type CarryForwardPrefill = z.infer<typeof CarryForwardPrefillSchema>;

// ─── Recorded decisions on the client-facing view response (client-offer-decision-persistence) ───

/**
 * A recorded decision is always ACCEPTED or REJECTED — the DB's
 * offer_version_unit_decisions table never stores UNDECIDED (a row only
 * exists once a decision was made), so this response-side enum is narrower
 * than the write-side UnitDecisionSchema by construction, not by convention.
 */
export const RecordedUnitDecisionSchema = z.enum(['ACCEPTED', 'REJECTED']);
export type RecordedUnitDecision = z.infer<typeof RecordedUnitDecisionSchema>;

/**
 * A single per-unit decision recorded when a version was accepted, returned
 * on the client-facing view response. Uses `mediaUnitId` (not `unitId`) to
 * match every other read-side schema in this file (UnitDecisionsListItemSchema,
 * ReleasedUnitSchema, CarryForwardPrefillEntrySchema); `periods` follows the
 * same optional-iff-ACCEPTED convention as UnitDecisionEntrySchema.
 */
export const RecordedUnitDecisionEntrySchema = z
  .object({
    mediaUnitId: z.number().int().positive(),
    decision: RecordedUnitDecisionSchema,
    periods: z.array(UnitDecisionPeriodSchema).optional(),
  })
  .strict()
  .refine(periodsRequiredIffAccepted, PERIODS_REQUIRED_IFF_ACCEPTED_ISSUE);
export type RecordedUnitDecisionEntry = z.infer<typeof RecordedUnitDecisionEntrySchema>;

export const RecordedUnitDecisionsSchema = z.array(RecordedUnitDecisionEntrySchema);
export type RecordedUnitDecisions = z.infer<typeof RecordedUnitDecisionsSchema>;
